import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../api/client';
import { useNotificationDispatch } from '../../context/NotificationContext';
import { TableRowSkeleton } from '../../components/admin/TableRowSkeleton';

// Ícones
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-9-4l5-5m-5 5l-5 5" /></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;
const PlusIcon = ({ className }: { className: string }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>;

interface Category {
    id: number;
    name: string;
    slug: string;
    icon: string;
    pluginCount: number;
}

const slugify = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export default function AdminCategoriesPage() {
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [currentCategory, setCurrentCategory] = useState<Partial<Category>>({ name: '', slug: '', icon: '' });
    const [formStatus, setFormStatus] = useState({ loading: false, error: '' });
    const { showNotification } = useNotificationDispatch();

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/admin/categories');
            setCategories(response.data);
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Falha ao buscar categorias.';
            showNotification(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleEdit = (category: Category) => {
        setIsEditing(true);
        setCurrentCategory(category);
    };

    const handleCreateNew = () => {
        setIsEditing(false);
        setCurrentCategory({ name: '', slug: '', icon: '' });
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("ATENÇÃO: Deseja realmente excluir esta categoria?")) return;

        try {
            await apiClient.delete(`/admin/categories/${id}`);
            showNotification('Categoria excluída com sucesso!', 'success');
            fetchCategories();
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Falha ao excluir a categoria.';
            showNotification(errorMessage, 'error');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormStatus({ loading: true, error: '' });

        try {
            if (isEditing) {
                await apiClient.put(`/admin/categories/${currentCategory.id}`, currentCategory);
            } else {
                await apiClient.post('/admin/categories', currentCategory);
            }
            showNotification(`Categoria ${isEditing ? 'atualizada' : 'criada'} com sucesso!`, 'success');
            handleCreateNew();
            fetchCategories();
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Ocorreu um erro.';
            setFormStatus({ loading: false, error: errorMessage });
        } finally {
            setFormStatus(prev => ({ ...prev, loading: false }));
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCurrentCategory(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'name' && !isEditing && { slug: slugify(value) })
        }));
    };

    return (
        <div className="space-y-8">
            <h1 className="text-3xl font-bold text-text-primary">Gerenciar Categorias</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1">
                    <div className="bg-white p-6 rounded-lg shadow-subtle sticky lg:top-20">
                        <div className='flex justify-between items-center border-b pb-3 mb-4'>
                            <h2 className="text-xl font-bold text-text-primary">{isEditing ? 'Editar Categoria' : 'Adicionar Nova'}</h2>
                            {isEditing && (
                                <button onClick={handleCreateNew} className="text-sm text-brand-blue hover:underline flex items-center gap-1">
                                    <PlusIcon className='w-4 h-4' /> Adicionar Nova
                                </button>
                            )}
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="name" className="form-label">Nome da Categoria</label>
                                <input type="text" name="name" value={currentCategory.name || ''} onChange={handleChange} required className="form-input" />
                            </div>
                            <div>
                                <label htmlFor="slug" className="form-label">Slug (URL)</label>
                                <input type="text" name="slug" value={currentCategory.slug || ''} onChange={handleChange} required className="form-input" readOnly={isEditing} />
                            </div>
                            <div>
                                <label htmlFor="icon" className="form-label">Ícone (Emoji)</label>
                                <input type="text" name="icon" value={currentCategory.icon || ''} onChange={handleChange} className="form-input" placeholder="Ex: 💰" />
                            </div>
                            {formStatus.error && <p className="text-red-500 text-sm mt-2">{formStatus.error}</p>}
                            <button type="submit" disabled={formStatus.loading} className="w-full bg-brand-blue text-white py-2.5 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400">
                                {formStatus.loading ? 'Salvando...' : (isEditing ? 'Salvar Alterações' : 'Criar Categoria')}
                            </button>
                        </form>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <h2 className="text-xl font-bold text-text-primary mb-4">Lista de Categorias ({categories.length})</h2>
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <table className="min-w-full hidden md:table">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="th-cell">Nome / Slug</th>
                                    <th className="th-cell text-center">Ícone</th>
                                    <th className="th-cell text-center">Plugins</th>
                                    <th className="th-cell text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} columns={4} />)
                                ) : (
                                    categories.map(cat => (
                                        <tr key={cat.id}>
                                            <td className="td-cell"><p className="font-medium text-gray-900">{cat.name}</p><p className="text-xs text-gray-500">{cat.slug}</p></td>
                                            <td className="td-cell text-center text-xl">{cat.icon}</td>
                                            <td className="td-cell text-center font-bold text-gray-700">{cat.pluginCount}</td>
                                            <td className="td-cell text-right">
                                                <button onClick={() => handleEdit(cat)} className="text-indigo-600 hover:text-indigo-900"><EditIcon /></button>
                                                <button onClick={() => handleDelete(cat.id)} className="text-red-600 hover:text-red-900 ml-4"><TrashIcon /></button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}